import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api.js";
import {
  MODERATION_FILTERS,
  moderationBrowserParams,
  moderationQueueApiPath,
  resolveModerationFilter,
  resolveModerationRecipeId,
} from "./moderation-filters.js";

/** Enhetskoder → svenska förkortningar (admin-vyn ska inte blanda språk). */
const UNIT_SV: Record<string, string> = {
  GRAM: "g",
  KILOGRAM: "kg",
  MILLILITER: "ml",
  DECILITER: "dl",
  LITER: "l",
  TEASPOON: "tsk",
  TABLESPOON: "msk",
  CUP_US: "cup",
  FLUID_OUNCE_US: "fl oz",
  OUNCE: "oz",
  POUND: "lb",
  COUNT: "st",
  PORTION: "port",
  PINCH: "nypa",
  SLICE: "skiva",
  CLOVE: "klyfta",
  CAN: "burk",
  PACKAGE: "förp",
};
const unitSv = (unit: string) => UNIT_SV[unit] ?? unit.toLowerCase();

type ModerationCategory =
  "safety_invalid" | "rejected_review" | "pending_review" | "published_review" | "handled";

interface RecipeIngredient {
  displayNameSv: string;
  quantity: number;
  unit: string;
  optional: boolean;
}

interface RecipeStep {
  stepNumber: number;
  instructionSv: string;
}

interface ModerationRecipe {
  id: string;
  titleSv: string;
  descriptionSv: string;
  status: string;
  verificationStatus: string;
  creatorDisplayName: string | null;
  moderationNote: string | null;
  createdAt: string;
  updatedAt: string;
  portions: number;
  prepTimeMinutes: number;
  cookTimeMinutes: number;
  category: ModerationCategory;
  reason: string;
  ingredients: RecipeIngredient[];
  steps: RecipeStep[];
}

interface ModerationOverview {
  recipes: ModerationRecipe[];
  counts: {
    all: number;
    safety: number;
    pending: number;
    publishedReview: number;
  };
  filter: string;
}

function categoryLabel(category: ModerationCategory): string {
  switch (category) {
    case "safety_invalid":
      return "Säkerhetsavvikelse";
    case "rejected_review":
      return "Automatiskt avslaget";
    case "pending_review":
      return "Väntar på beslut";
    case "published_review":
      return "Publicerad – stickprov";
    default:
      return "Hanterad";
  }
}

function stateLabel(value: string): string {
  const labels: Record<string, string> = {
    draft: "utkast",
    submitted: "inskickat",
    ai_checked: "AI-kontrollerat",
    in_moderation: "under granskning",
    published: "publicerat",
    rejected: "avslaget",
    unverified: "ej verifierat",
    community: "community-verifierat",
    verified: "verifierat",
    editorial: "redaktionellt verifierat",
  };
  return labels[value] ?? value.replaceAll("_", " ");
}

export function ModerationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = resolveModerationFilter(searchParams.get("filter"));
  const targetRecipeId = resolveModerationRecipeId(searchParams.get("recipe"));
  const activeFilter = MODERATION_FILTERS.find((item) => item.id === filter)!;
  const [data, setData] = useState<ModerationOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setData(null);
    api<ModerationOverview>(moderationQueueApiPath(filter, targetRecipeId))
      .then((result) => {
        if (alive) setData(result);
      })
      .catch((caught: unknown) => {
        if (alive) setError(caught instanceof Error ? caught.message : String(caught));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [filter, reloadKey, targetRecipeId]);

  const act = async (
    id: string,
    action: "approve" | "reject" | "request_changes",
  ): Promise<void> => {
    const note =
      action === "approve" ? undefined : (prompt("Motivering till skaparen:") ?? undefined);
    if (action !== "approve" && note === undefined) return;
    setActingOn(id);
    setError(null);
    try {
      await api(`/admin/v1/moderation/recipes/${id}`, {
        method: "POST",
        body: { action, note },
      });
      setReloadKey((current) => current + 1);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div className="page-stack">
      <header className="page-header">
        <div className="eyebrow">Innehållssäkerhet</div>
        <h1>Receptgranskning</h1>
        <p>Varje ärende visar varför receptet finns här och vilket beslut som behövs.</p>
      </header>

      <div className="filter-tabs" role="tablist" aria-label="Filtrera recept">
        {MODERATION_FILTERS.map((item) => (
          <button
            key={item.id}
            className={filter === item.id ? "active" : ""}
            onClick={() => setSearchParams(moderationBrowserParams(item.id))}
            role="tab"
            aria-selected={filter === item.id}
          >
            {item.label}
            <span>{data?.counts[item.countKey] ?? "–"}</span>
          </button>
        ))}
      </div>

      <section className={`moderation-filter-summary ${filter}`} aria-live="polite">
        <strong>{activeFilter.title}</strong>
        <p>{activeFilter.description}</p>
        {targetRecipeId && (
          <p className="moderation-target-note">
            Visar endast receptet som valdes i EOC. Byt kö ovan för att lämna detaljläget.
          </p>
        )}
      </section>

      {error && <div className="notice danger">{error}</div>}
      {loading && <div className="panel loading-panel">Laddar granskningskön …</div>}
      {!loading && data?.recipes.length === 0 && (
        <div className="panel empty-state">
          <h2>{targetRecipeId ? "Receptet finns inte längre i kön" : "Kön är tom"}</h2>
          <p>
            {targetRecipeId
              ? "Det kan redan ha hanterats eller inte längre uppfylla det valda filtret."
              : "Alla recept i det valda filtret är hanterade."}
          </p>
        </div>
      )}

      {!loading &&
        data?.recipes.map((recipe) => (
          <article
            className={`panel moderation-card ${recipe.category}${
              recipe.id === targetRecipeId ? " deep-linked" : ""
            }`}
            key={recipe.id}
          >
            <div className="moderation-card-header">
              <div>
                <div className="moderation-title-row">
                  <h2>{recipe.titleSv}</h2>
                  <span className={`status-chip ${recipe.category}`}>
                    {categoryLabel(recipe.category)}
                  </span>
                </div>
                <p className="moderation-reason">{recipe.reason}</p>
                <div className="moderation-status">
                  Status: {stateLabel(recipe.status)} · Verifiering:{" "}
                  {stateLabel(recipe.verificationStatus)} · Uppdaterad{" "}
                  {new Date(recipe.updatedAt).toLocaleString("sv-SE")}
                </div>
                <div className="muted">
                  Inskickat av {recipe.creatorDisplayName ?? "okänd"} ·{" "}
                  {new Date(recipe.createdAt).toLocaleString("sv-SE")}
                </div>
              </div>
              <div className="moderation-actions">
                <button
                  className="primary"
                  disabled={actingOn === recipe.id}
                  onClick={() => void act(recipe.id, "approve")}
                >
                  Godkänn
                </button>
                <button
                  className="ghost"
                  disabled={actingOn === recipe.id}
                  onClick={() => void act(recipe.id, "request_changes")}
                >
                  Begär ändringar
                </button>
                <button
                  className="ghost danger"
                  disabled={actingOn === recipe.id}
                  onClick={() => void act(recipe.id, "reject")}
                >
                  Avslå
                </button>
              </div>
            </div>

            <p>{recipe.descriptionSv}</p>
            <div className="muted moderation-timing">
              {recipe.portions} portioner · {recipe.prepTimeMinutes + recipe.cookTimeMinutes} min
            </div>
            <div className="moderation-content">
              <div>
                <strong>Ingredienser</strong>
                <ul>
                  {recipe.ingredients.map((ingredient, index) => (
                    <li key={`${ingredient.displayNameSv}-${index}`}>
                      {ingredient.quantity} {unitSv(ingredient.unit)} {ingredient.displayNameSv}
                      {ingredient.optional ? " (valfritt)" : ""}
                    </li>
                  ))}
                  {recipe.ingredients.length === 0 && (
                    <li className="muted">(inga kopplade ingredienser)</li>
                  )}
                </ul>
              </div>
              <div>
                <strong>Gör så här</strong>
                <ol>
                  {recipe.steps.map((step) => (
                    <li key={step.stepNumber}>{step.instructionSv}</li>
                  ))}
                  {recipe.steps.length === 0 && <li className="muted">(inga steg)</li>}
                </ol>
              </div>
            </div>
            {recipe.moderationNote && (
              <div className="moderation-note">
                <strong>AI-kontroll och dubblettkontroll</strong>
                <p>{recipe.moderationNote}</p>
              </div>
            )}
          </article>
        ))}
    </div>
  );
}
